import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { verifySingleCertificate } from '../utils/api';
import { toast } from 'react-toastify';

const VerifyCertificate = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [certificateData, setCertificateData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Get certificate number from URL params
    // Handle both ?cert=CERT-XXX and ?CERT-XXX formats
    const certificateNumber = searchParams.get('cert') || window.location.search.replace('?', '').replace(/=$/, '') || searchParams.toString().replace(/=$/, '');

    useEffect(() => {
        document.title = 'Certificate Verification - KPCA';
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
            metaDescription.setAttribute('content', 'Verify KPCA membership certificate and view company details.');
        }
    }, []);

    useEffect(() => {
        if (certificateNumber) {
            fetchCertificateDetails();
        } else {
            setError('No certificate number provided');
            setLoading(false);
        }
    }, [certificateNumber]);

    const fetchCertificateDetails = async () => {
        try {
            setLoading(true);
            setError(null);

            console.log('Fetching certificate details for:', certificateNumber);

            const data = await verifySingleCertificate(certificateNumber);

            if (data.success && data.isValid) {
                // Map the API response to our expected format
                const mappedData = {
                    // Company information from API data
                    company_name: data.data.company_name,
                    membership_number: data.data.membership_number,
                    contact_person: data.data.contact_person,
                    registration_number: data.data.registration_number || 'Not provided',
                    business_address: data.data.business_address || 'Not provided',
                    status: data.data.verification_status || 'pending',
                    verification_status: data.data.verification_status || 'pending',

                    // Registration details
                    registration_date: data.data.registration_date,
                    updated_at: data.data.updated_at,
                    profile_completion_percentage: data.data.profile_completion_percentage || 100,

                    // Certificate details
                    certificate_number: data.data.certificate.certificate_number,
                    certificate_type: data.data.certificate.certificate_type,
                    issue_date: data.data.certificate.issue_date,
                    valid_until: data.data.certificate.expiry_date,
                    expiry_date: data.data.certificate.expiry_date,

                    // Verification info
                    is_expired: data.data.verification.is_expired,
                    verified_at: data.data.verification.verified_at
                };

                setCertificateData(mappedData);
                toast.success('Certificate verified successfully');
            } else {
                setError(data.message || 'Certificate not found or invalid');
                toast.error(data.message || 'Certificate verification failed');
            }
        } catch (err) {
            console.error('Error fetching certificate:', err);
            setError('Failed to verify certificate. Please try again.');
            toast.error('Failed to verify certificate');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusClasses = {
            'approved': 'bg-green-100 text-green-800',
            'pending': 'bg-yellow-100 text-yellow-800',
            'rejected': 'bg-red-100 text-red-800',
            'active': 'bg-green-100 text-green-800',
            'inactive': 'bg-gray-100 text-gray-800'
        };

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
                {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown'}
            </span>
        );
    };

    const getProfileCompletionBadge = (percentage) => {
        const completionClass = percentage >= 100 ? 'bg-green-100 text-green-800' :
            percentage >= 75 ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800';

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${completionClass}`}>
                {percentage}%
            </span>
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not available';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 py-12">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="mt-4 text-gray-600">Verifying certificate...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !certificateData) {
        return (
            <div className="min-h-screen bg-gray-50 py-12">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                        </div>
                        <h3 className="mt-4 text-lg font-medium text-gray-900">Certificate Verification Failed</h3>
                        <p className="mt-2 text-sm text-gray-500">
                            {error || 'The certificate could not be verified. Please check the certificate number and try again.'}
                        </p>
                        <div className="mt-6">
                            <button
                                onClick={() => navigate('/verify-member')}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                Manual Verification
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-12">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                        <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h1 className="mt-4 text-3xl font-bold text-gray-900">Certificate Verified</h1>
                    <p className="mt-2 text-lg text-gray-600">
                        Certificate Number: <span className="font-medium text-blue-600">{certificateNumber}</span>
                    </p>
                </div>

                {/* Company Information Card */}
                <div className="bg-white shadow rounded-lg mb-6">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-medium text-gray-900 flex items-center">
                            <svg className="h-5 w-5 text-gray-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            Company Information
                        </h2>
                    </div>
                    <div className="px-6 py-4">
                        <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                            <div>
                                <dt className="text-sm font-medium text-gray-500">Company Name</dt>
                                <dd className="mt-1 text-sm text-gray-900 font-semibold">
                                    {certificateData.company_name || 'Not provided'}
                                </dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500">Membership Number</dt>
                                <dd className="mt-1 text-sm text-gray-900 font-semibold">
                                    {certificateData.membership_number || 'Not assigned'}
                                </dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500">Registration Number</dt>
                                <dd className="mt-1 text-sm text-gray-900">
                                    {certificateData.registration_number || 'Not provided'}
                                </dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500">Business Address</dt>
                                <dd className="mt-1 text-sm text-gray-900">
                                    {certificateData.business_address || 'Not provided'}
                                </dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500">Status</dt>
                                <dd className="mt-1">
                                    {getStatusBadge(certificateData.status)}
                                </dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500">Verification Status</dt>
                                <dd className="mt-1">
                                    {getStatusBadge(certificateData.verification_status || certificateData.status)}
                                </dd>
                            </div>
                        </dl>
                    </div>
                </div>

                {/* KPCA Logo/Image */}
                <div className="bg-white shadow rounded-lg mb-6">
                    <div className="px-6 py-4 text-center">
                        <img
                            src="/logo.jpg"
                            alt="KPCA"
                            className="mx-auto h-16 w-auto"
                            onError={(e) => {
                                e.target.style.display = 'none';
                            }}
                        />
                    </div>
                </div>


                {/* Certificate Details */}
                {certificateData.certificate_number && (
                    <div className="bg-white shadow rounded-lg mb-6">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h2 className="text-lg font-medium text-gray-900 flex items-center">
                                <svg className="h-5 w-5 text-gray-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                                Certificate Details
                            </h2>
                        </div>
                        <div className="px-6 py-4">
                            <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">Certificate Number</dt>
                                    <dd className="mt-1 text-sm text-gray-900 font-semibold">
                                        {certificateData.certificate_number}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">Issue Date</dt>
                                    <dd className="mt-1 text-sm text-gray-900">
                                        {formatDate(certificateData.issue_date)}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">Valid Until</dt>
                                    <dd className="mt-1 text-sm text-gray-900">
                                        {formatDate(certificateData.valid_until || certificateData.expiry_date)}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">Certificate Type</dt>
                                    <dd className="mt-1 text-sm text-gray-900">
                                        {certificateData.certificate_type || 'Membership Certificate'}
                                    </dd>
                                </div>
                            </dl>
                        </div>
                    </div>
                )}

                {/* Footer Actions */}
                <div className="text-center space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:justify-center">
                    <button
                        onClick={() => navigate('/verify-member')}
                        className="w-full sm:w-auto inline-flex justify-center items-center px-6 py-3 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Verify Another Certificate
                    </button>
                    <button
                        onClick={() => navigate('/')}
                        className="w-full sm:w-auto inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Back to Home
                    </button>
                </div>
            </div>
        </div>
    );
};

export default VerifyCertificate;
